import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getServiceBySlug, getGalleryForService, submitWebform } from '../lib/cmsClient';
import { COMPANY_INFO, SERVICES, REVIEWS } from '../data/staticData';
import LightboxModal from '../components/LightboxModal';
import FileUploadField from '../components/FileUploadField';

function ServiceFaqAccordion({ items, formatInlineText }) {
  const [openIndices, setOpenIndices] = useState([0]);

  const toggle = (i) => {
    setOpenIndices((prev) =>
      prev.includes(i) ? prev.filter((idx) => idx !== i) : [...prev, i]
    );
  };

  return (
    <div className="space-y-3 my-4">
      {items.map((rawItem, i) => {
        const match = rawItem.match(/^\*\*([^*]+)\*\*[:\s–-]+(.*)$/s);
        const question = match ? match[1].trim() : rawItem;
        const answer = match ? match[2].trim() : '';
        const isOpen = openIndices.includes(i);

        return (
          <div
            key={i}
            className="border border-stone-200 bg-white shadow-xs overflow-hidden transition-all duration-200 hover:border-stone-300"
          >
            <button
              type="button"
              onClick={() => toggle(i)}
              className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 font-bold text-stone-900 text-sm sm:text-base hover:bg-stone-50 transition-colors cursor-pointer"
              aria-expanded={isOpen}
            >
              <span className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-red-50 text-[#BE0F12] flex items-center justify-center text-xs font-black shrink-0">
                  <i className="fa-solid fa-question text-[10px]" />
                </span>
                <span>{question}</span>
              </span>
              <i
                className={`fa-solid fa-chevron-down text-stone-400 text-xs sm:text-sm transition-transform duration-200 shrink-0 ${
                  isOpen ? 'rotate-180 text-[#BE0F12]' : ''
                }`}
              />
            </button>
            {isOpen && answer && (
              <div className="px-5 pb-5 pt-2 text-stone-600 text-sm sm:text-base leading-relaxed border-t border-stone-100 sm:pl-14">
                {formatInlineText(answer)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Form State
  const [formState, setFormState] = useState({
    Navn: '',
    Mail: '',
    Mobil: '',
    Adresse: '',
    Postnummer: '',
    Besked: '',
    Finansiering: false,
    AccepterBetingelser: false,
    website_url: '' // Honeypot
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    async function load() {
      const activeSlug = slug || 'betontag';
      const s = await getServiceBySlug(activeSlug);
      setService(s || SERVICES.find(srv => srv.slug === activeSlug) || SERVICES[0]);

      const gallery = await getGalleryForService(activeSlug);
      setGalleryImages(gallery);
    }
    load();
  }, [slug]);

  if (!service) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center space-y-4">
        <h1 className="text-3xl font-bold text-stone-900">Indlæser...</h1>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formState.AccepterBetingelser) {
      alert('Du skal acceptere vilkårene for privatlivspolitik for at sende.');
      return;
    }

    setSubmitting(true);
    setSubmitStatus(null);

    const beskedText = `[YDELSE FORESPØRGSEL: ${service.title}]\nBesked: ${formState.Besked}`;
    let res;
    if (files.length > 0) {
      const formData = new FormData();
      formData.append('Navn', formState.Navn);
      formData.append('Mail', formState.Mail);
      formData.append('email', formState.Mail);
      formData.append('Mobil', formState.Mobil);
      formData.append('Adresse', formState.Adresse);
      formData.append('Postnummer', formState.Postnummer);
      formData.append('Besked', beskedText);
      formData.append('Finansiering', formState.Finansiering ? 'Ja' : 'Nej');
      formData.append('website_url', formState.website_url); // Honeypot
      files.forEach((file) => {
        formData.append('billeder', file);
      });
      res = await submitWebform(formData);
    } else {
      const payload = {
        Navn: formState.Navn,
        Mail: formState.Mail,
        email: formState.Mail,
        Mobil: formState.Mobil,
        Adresse: formState.Adresse,
        Postnummer: formState.Postnummer,
        Besked: beskedText,
        Finansiering: formState.Finansiering,
        website_url: formState.website_url // Honeypot
      };
      res = await submitWebform(payload);
    }
    setSubmitting(false);

    if (res.ok) {
      setSubmitStatus('success');
      setFiles([]);
      setFormState({
        Navn: '',
        Mail: '',
        Mobil: '',
        Adresse: '',
        Postnummer: '',
        Besked: '',
        Finansiering: false,
        AccepterBetingelser: false,
        website_url: ''
      });
    } else {
      setSubmitStatus('error');
    }
  };

  const isEnergy = service.category === 'Energiløsninger' || service.slug === 'isolering' || service.slug === 'vinduer-og-doere' || service.slug === 'solceller';

  const formatInlineText = (text) => {
    if (!text) return null;
    const parts = [];
    const regex = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
    let lastIndex = 0;
    let match;
    let key = 0;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      const token = match[0];
      if (token.startsWith('**') && token.endsWith('**')) {
        parts.push(<strong key={key++} className="font-bold text-stone-900">{token.slice(2, -2)}</strong>);
      } else if (token.startsWith('[') && token.includes('](')) {
        const linkText = token.slice(1, token.indexOf(']('));
        const linkUrl = token.slice(token.indexOf('](') + 2, -1);
        parts.push(
          <Link key={key++} to={linkUrl} className="text-[#BE0F12] font-semibold underline hover:text-stone-900">
            {linkText}
          </Link>
        );
      }
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    return parts.length > 0 ? parts : text;
  };

  const renderFormattedContent = (content) => {
    if (!content) return null;
    if (content.trim().startsWith('<')) {
      return (
        <div
          className="space-y-4 leading-relaxed text-sm sm:text-base text-stone-700 [&>h2]:text-2xl [&>h2]:sm:text-3xl [&>h2]:font-extrabold [&>h2]:text-stone-900 [&>h2]:uppercase [&>h2]:pt-6 [&>h3]:text-xl [&>h3]:sm:text-2xl [&>h3]:font-bold [&>h3]:text-stone-900 [&>h3]:uppercase [&>h3]:pt-5 [&>p]:leading-relaxed [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-2 [&>ol]:list-decimal [&>ol]:pl-5 [&>ol]:space-y-2 [&>a]:text-[#BE0F12] [&>a]:underline [&_a]:text-[#BE0F12] [&_a]:underline"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }

    // Normaliser markdown: skil overskrifter og lister fra hinanden så intet sluges i en overskrift
    const normalized = content
      .replace(/\r\n/g, '\n')
      .replace(/^(#{1,6}\s+[^\n]+)\n(?!\n)/gm, '$1\n\n')
      .replace(/([^\n])\n([-*]|\d+\.)\s/g, '$1\n\n$2 ');

    const rawBlocks = normalized.split('\n\n');
    let lastHeading = '';

    return (
      <div className="space-y-4 text-stone-700 leading-relaxed text-sm sm:text-base">
        {rawBlocks.map((block, idx) => {
          const trimmed = block.trim();
          if (!trimmed) return null;

          if (trimmed.startsWith('#### ')) {
            const headingText = trimmed.replace(/^####\s+/, '').split('\n')[0];
            lastHeading = headingText;
            return (
              <h4 key={idx} className="text-base sm:text-lg font-bold text-stone-900 pt-3">
                {headingText}
              </h4>
            );
          }
          if (trimmed.startsWith('### ')) {
            const headingText = trimmed.replace(/^###\s+/, '').split('\n')[0];
            lastHeading = headingText;
            return (
              <h3 key={idx} className="text-lg sm:text-2xl font-bold text-stone-900 pt-4 uppercase">
                {headingText}
              </h3>
            );
          }
          if (trimmed.startsWith('## ')) {
            const headingText = trimmed.replace(/^##\s+/, '').split('\n')[0];
            lastHeading = headingText;
            return (
              <h2 key={idx} className="text-xl sm:text-3xl font-extrabold text-stone-900 pt-6 uppercase">
                {headingText}
              </h2>
            );
          }
          if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            const items = trimmed.split('\n').map((line) => line.replace(/^[-*]\s*/, '').trim()).filter(Boolean);
            const isFaqHeading = /faq|ofte stillede spørgsmål/i.test(lastHeading);
            const hasFaqQuestions = items.length > 0 && items.every((item) => /^\*\*[^*]+\?\*\*[:\s–-]/.test(item));

            if (isFaqHeading || hasFaqQuestions) {
              return (
                <ServiceFaqAccordion
                  key={idx}
                  items={items}
                  formatInlineText={formatInlineText}
                />
              );
            }

            return (
              <ul key={idx} className="list-disc pl-5 space-y-2 text-stone-700 my-2">
                {items.map((item, i) => (
                  <li key={i}>{formatInlineText(item)}</li>
                ))}
              </ul>
            );
          }
          if (/^\d+\.\s/.test(trimmed)) {
            const items = trimmed.split('\n').map((line) => line.replace(/^\d+\.\s*/, '').trim()).filter(Boolean);
            return (
              <ol key={idx} className="list-decimal pl-5 space-y-2 text-stone-700 my-2">
                {items.map((item, i) => (
                  <li key={i}>{formatInlineText(item)}</li>
                ))}
              </ol>
            );
          }
          return (
            <p key={idx} className="leading-relaxed">
              {formatInlineText(trimmed)}
            </p>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-white">
      {/* Top Breadcrumb & Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12">
        <div className="flex items-center gap-2 text-xs text-stone-500 pb-4">
          <Link to="/" className="hover:text-[#BE0F12]">Forside</Link>
          <span>/</span>
          <Link to={isEnergy ? '/energiloesninger' : '/nyt-tag'} className="hover:text-[#BE0F12]">
            {isEnergy ? 'Energiløsninger' : 'Nyt tag'}
          </Link>
          <span>/</span>
          <span className="text-stone-900 font-semibold">{service.title}</span>
        </div>
      </div>

      {/* Main 2-column Grid Layout (Content left, Lasse/Beregner right) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Main Left Content Area */}
          <div className="lg:col-span-8 space-y-8">
            <h1 className="text-3xl sm:text-5xl font-extrabold text-stone-900 tracking-tight uppercase">
              {service.title}
            </h1>

            {/* Featured Image */}
            <div className="w-full overflow-hidden border border-stone-200">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-auto max-h-[460px] object-cover"
              />
            </div>

            {/* Formatted Content */}
            <div className="prose-stone max-w-none">
              {renderFormattedContent(service.content)}
            </div>

            {/* Connected CMS Gallery */}
            {galleryImages && galleryImages.length > 0 && (
              <div className="space-y-4 pt-6">
                <h3 className="text-xl font-bold uppercase text-stone-900">
                  Billeder fra udførte {service.title}-projekter
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {galleryImages.map((imgUrl, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }}
                      className="h-44 overflow-hidden border border-stone-200 hover:border-[#BE0F12] transition-all group relative cursor-pointer"
                    >
                      <img
                        src={imgUrl}
                        alt={`Galleri Billede ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/30 transition-colors flex items-center justify-center">
                        <i className="fa-solid fa-magnifying-glass text-white opacity-0 group-hover:opacity-100 transition-opacity text-xl"></i>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Callout box: Få et uforpligtende tilbud via vores prisberegner */}
            {service.slug === 'vinduer-og-doere' ? (
              <div className="bg-[#1c1c1c] text-white p-6 sm:p-8 flex flex-col lg:flex-row items-center justify-between gap-6 border-l-4 border-[#BE0F12]">
                <div className="space-y-2 text-center lg:text-left">
                  <div className="inline-block px-2.5 py-0.5 bg-[#BE0F12]/20 border border-[#BE0F12]/40 text-[#ff6b6b] text-xs font-bold uppercase tracking-wider mb-1">
                    Online beregnere
                  </div>
                  <h3 className="text-xl sm:text-2xl font-extrabold uppercase">
                    Prisberegnere: Loftisolering & Nyt tag
                  </h3>
                  <p className="text-sm text-stone-300 max-w-xl">
                    Vores online prisberegnere dækker loftisolering og nyt tag. Ønsker du en pris på nye vinduer og døre, udarbejder vi gerne et uforpligtende, skræddersyet tilbud tilpasset din bolig.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full lg:w-auto">
                  <Link
                    to="/p/prisberegner-energiloesninger"
                    className="w-full sm:w-auto text-center px-5 py-3.5 bg-[#BE0F12] hover:bg-red-700 text-white font-extrabold text-xs sm:text-sm uppercase tracking-wider transition-colors shadow-md"
                  >
                    Prisberegner: Loftisolering
                  </Link>
                  <Link
                    to="/prisberegner"
                    className="w-full sm:w-auto text-center px-5 py-3.5 bg-stone-800 hover:bg-stone-700 text-white font-extrabold text-xs sm:text-sm uppercase tracking-wider transition-colors shadow-md border border-stone-600"
                  >
                    Prisberegner: Nyt tag
                  </Link>
                </div>
              </div>
            ) : service.slug === 'solceller' ? (
              <div className="bg-[#1c1c1c] text-white p-6 sm:p-8 flex flex-col lg:flex-row items-center justify-between gap-6 border-l-4 border-[#BE0F12]">
                <div className="space-y-2 text-center lg:text-left">
                  <div className="inline-block px-2.5 py-0.5 bg-[#BE0F12]/20 border border-[#BE0F12]/40 text-[#ff6b6b] text-xs font-bold uppercase tracking-wider mb-1">
                    Online beregnere
                  </div>
                  <h3 className="text-xl sm:text-2xl font-extrabold uppercase">
                    Prisberegnere: Nyt tag & Loftisolering
                  </h3>
                  <p className="text-sm text-stone-300 max-w-xl">
                    Overvejer du solceller i kombination med et nyt tag eller efterisolering? Prøv vores online beregnere – eller kontakt os for et skræddersyet tilbud på dit solcelleprojekt.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full lg:w-auto">
                  <Link
                    to="/prisberegner"
                    className="w-full sm:w-auto text-center px-5 py-3.5 bg-[#BE0F12] hover:bg-red-700 text-white font-extrabold text-xs sm:text-sm uppercase tracking-wider transition-colors shadow-md"
                  >
                    Prisberegner: Nyt tag
                  </Link>
                  <Link
                    to="/p/prisberegner-energiloesninger"
                    className="w-full sm:w-auto text-center px-5 py-3.5 bg-stone-800 hover:bg-stone-700 text-white font-extrabold text-xs sm:text-sm uppercase tracking-wider transition-colors shadow-md border border-stone-600"
                  >
                    Prisberegner: Loftisolering
                  </Link>
                </div>
              </div>
            ) : service.slug === 'isolering' ? (
              <div className="bg-[#1c1c1c] text-white p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 border-l-4 border-[#BE0F12]">
                <div className="space-y-2 text-center sm:text-left">
                  <h3 className="text-xl sm:text-2xl font-extrabold uppercase">
                    Få et uforpligtende tilbud – Prisberegner: Loftisolering
                  </h3>
                  <p className="text-sm text-stone-300 max-w-xl">
                    Ud fra 4 simple spørgsmål beregner vi med det samme en vejledende pris til dig på efterisolering af dit loft. Hurtigt og nemt!
                  </p>
                </div>
                <Link
                  to="/p/prisberegner-energiloesninger"
                  className="whitespace-nowrap px-6 py-3.5 bg-[#BE0F12] hover:bg-red-700 text-white font-extrabold text-sm uppercase tracking-wider transition-colors shadow-md shrink-0"
                >
                  Prisberegner: Loftisolering
                </Link>
              </div>
            ) : ['tagreparation', 'tagrender', 'kviste-og-skotrender', 'zink-og-kobber'].includes(service.slug) ? (
              <div className="bg-[#1c1c1c] text-white p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 border-l-4 border-[#BE0F12]">
                <div className="space-y-2 text-center sm:text-left">
                  <h3 className="text-xl sm:text-2xl font-extrabold uppercase">
                    Prisberegner: Nyt tag
                  </h3>
                  <p className="text-sm text-stone-300 max-w-xl">
                    Overvejer du et komplet nyt tag frem for reparation eller deludskiftning? Prøv vores online prisberegner for nyt tag – eller kontakt os for et uforpligtende tilbud på {service.title.toLowerCase()}.
                  </p>
                </div>
                <Link
                  to="/prisberegner"
                  className="whitespace-nowrap px-6 py-3.5 bg-[#BE0F12] hover:bg-red-700 text-white font-extrabold text-sm uppercase tracking-wider transition-colors shadow-md shrink-0"
                >
                  Prisberegner: Nyt tag
                </Link>
              </div>
            ) : (
              <div className="bg-[#1c1c1c] text-white p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 border-l-4 border-[#BE0F12]">
                <div className="space-y-2 text-center sm:text-left">
                  <h3 className="text-xl sm:text-2xl font-extrabold uppercase">
                    Få et uforpligtende tilbud – Prisberegner: Nyt tag
                  </h3>
                  <p className="text-sm text-stone-300 max-w-xl">
                    Ud fra 4 simple spørgsmål beregner vi med det samme en uforpligtende pris til dig på nyt tag ({service.title}). Hurtigt og nemt!
                  </p>
                </div>
                <Link
                  to="/prisberegner"
                  className="whitespace-nowrap px-6 py-3.5 bg-[#BE0F12] hover:bg-red-700 text-white font-extrabold text-sm uppercase tracking-wider transition-colors shadow-md shrink-0"
                >
                  Prisberegner: Nyt tag
                </Link>
              </div>
            )}
          </div>

          {/* Right Sidebar (Lasse + Prisberegner) */}
          <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-36">
            
            {/* Prisberegner Quick Card */}
            {service.slug === 'vinduer-og-doere' ? (
              <div className="bg-stone-100 border border-stone-200 p-6 space-y-4">
                <div className="space-y-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-[#BE0F12]">Online beregnere</span>
                  <h3 className="text-lg font-bold text-stone-900 uppercase">
                    Prisberegnere
                  </h3>
                </div>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Vores online beregnere dækker loftisolering og nyt tag. Til nye vinduer og døre udarbejder vi et skræddersyet tilbud.
                </p>
                <div className="space-y-2 pt-1">
                  <Link
                    to="/p/prisberegner-energiloesninger"
                    className="block text-center w-full py-2.5 bg-[#BE0F12] hover:bg-red-700 text-white font-bold text-xs uppercase tracking-wider transition-colors shadow-xs"
                  >
                    Prisberegner: Loftisolering
                  </Link>
                  <Link
                    to="/prisberegner"
                    className="block text-center w-full py-2.5 bg-stone-800 hover:bg-stone-900 text-white font-bold text-xs uppercase tracking-wider transition-colors shadow-xs"
                  >
                    Prisberegner: Nyt tag
                  </Link>
                </div>
              </div>
            ) : service.slug === 'solceller' ? (
              <div className="bg-stone-100 border border-stone-200 p-6 space-y-4">
                <div className="space-y-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-[#BE0F12]">Online beregnere</span>
                  <h3 className="text-lg font-bold text-stone-900 uppercase">
                    Prisberegnere
                  </h3>
                </div>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Prøv vores online beregnere for nyt tag eller loftisolering i forbindelse med solcelleprojektet.
                </p>
                <div className="space-y-2 pt-1">
                  <Link
                    to="/prisberegner"
                    className="block text-center w-full py-2.5 bg-[#BE0F12] hover:bg-red-700 text-white font-bold text-xs uppercase tracking-wider transition-colors shadow-xs"
                  >
                    Prisberegner: Nyt tag
                  </Link>
                  <Link
                    to="/p/prisberegner-energiloesninger"
                    className="block text-center w-full py-2.5 bg-stone-800 hover:bg-stone-900 text-white font-bold text-xs uppercase tracking-wider transition-colors shadow-xs"
                  >
                    Prisberegner: Loftisolering
                  </Link>
                </div>
              </div>
            ) : service.slug === 'isolering' ? (
              <div className="bg-stone-100 border border-stone-200 p-6 space-y-4">
                <h3 className="text-lg font-bold text-stone-900 uppercase">
                  Prisberegner: Loftisolering
                </h3>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Beregn et hurtigt og uforpligtende overslag på efterisolering af dit loft via vores online beregner.
                </p>
                <Link
                  to="/p/prisberegner-energiloesninger"
                  className="block text-center w-full py-3 bg-[#BE0F12] hover:bg-red-700 text-white font-bold text-xs uppercase tracking-wider transition-colors shadow-sm"
                >
                  Prisberegner: Loftisolering
                </Link>
              </div>
            ) : (
              <div className="bg-stone-100 border border-stone-200 p-6 space-y-4">
                <h3 className="text-lg font-bold text-stone-900 uppercase">
                  Prisberegner: Nyt tag
                </h3>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Beregn et hurtigt og uforpligtende overslag på dit nye tag via vores online beregner.
                </p>
                <Link
                  to="/prisberegner"
                  className="block text-center w-full py-3 bg-[#BE0F12] hover:bg-red-700 text-white font-bold text-xs uppercase tracking-wider transition-colors shadow-sm"
                >
                  Prisberegner: Nyt tag
                </Link>
              </div>
            )}

            {/* Lasse Contact Card */}
            <div className="bg-white border border-stone-200 p-6 shadow-sm space-y-4">
              <div className="flex flex-row items-end justify-center gap-2 sm:gap-3 pb-2">
                <img
                  src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/har-du-sporgsmaal.png"
                  alt="Har du spørgsmål så tag fat i mig"
                  className="w-[42%] max-w-[130px] h-auto object-contain self-center"
                />
                <img
                  src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/lasse-sidebar.png"
                  alt="Lasse Vesterhouen - Dansk Tagbyg"
                  className="w-[58%] max-w-[180px] h-auto object-contain"
                />
              </div>

              <div className="space-y-1">
                <h4 className="text-lg font-extrabold text-stone-900 uppercase">
                  Kontakt mig, Lasse
                </h4>
                <p className="text-xs text-stone-500">
                  Direkte rådgivning og uforpligtende besigtigelse
                </p>
              </div>

              <div className="pt-2 border-t border-stone-100 space-y-2 text-sm text-stone-800">
                <div className="flex items-center gap-2">
                  <strong className="text-stone-900">Ring:</strong>
                  <a href={COMPANY_INFO.phoneTel} className="text-[#BE0F12] font-bold hover:underline">
                    {COMPANY_INFO.phoneFormatted}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <strong className="text-stone-900">Skriv:</strong>
                  <a href={COMPANY_INFO.emailMailto} className="text-[#BE0F12] hover:underline text-xs sm:text-sm">
                    {COMPANY_INFO.email}
                  </a>
                </div>
              </div>

              <div className="pt-2">
                <a
                  href="#kontakt-sektion"
                  className="block text-center w-full py-2.5 bg-stone-900 hover:bg-black text-white text-xs font-bold uppercase tracking-wider transition-colors"
                >
                  Kontakt os
                </a>
              </div>
            </div>

            {/* Quick Benefits list */}
            {service.benefits && service.benefits.length > 0 && (
              <div className="bg-stone-50 border border-stone-200 p-6 space-y-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-stone-900">
                  Fordele ved {service.title}:
                </h4>
                <ul className="space-y-2 text-xs text-stone-700">
                  {service.benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#BE0F12] mt-0.5 shrink-0"></i>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

          </div>

        </div>
      </div>

      {/* Customer Testimonials: Vi motiveres af glade kunder og gode resultater */}
      <section className="py-16 sm:py-20 bg-stone-100 border-t border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-stone-900 uppercase">
              Vi motiveres af glade kunder og gode resultater
            </h2>
            <div className="flex items-center justify-center gap-2 text-amber-500 text-sm">
              <div className="flex">
                <i className="fa-solid fa-star"></i>
                <i className="fa-solid fa-star"></i>
                <i className="fa-solid fa-star"></i>
                <i className="fa-solid fa-star"></i>
                <i className="fa-solid fa-star"></i>
              </div>
              <span className="font-bold text-stone-800">4,8 ud af 155 anmeldelser</span>
              <span className="text-stone-400">•</span>
              <span className="text-stone-600 font-medium">Finalist til Årets Håndværker 5 år i træk</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REVIEWS.slice(0, 6).map((rev) => (
              <div key={rev.id} className="bg-white p-6 border border-stone-200 shadow-sm flex flex-col justify-between space-y-4">
                <p className="text-xs sm:text-sm text-stone-700 italic leading-relaxed">
                  "{rev.quote}"
                </p>
                <div className="pt-3 border-t border-stone-100 flex items-center justify-between">
                  <span className="font-bold text-xs text-stone-900">{rev.author}, {rev.location}</span>
                  <div className="flex text-amber-500 text-xs">
                    <i className="fa-solid fa-star"></i>
                    <i className="fa-solid fa-star"></i>
                    <i className="fa-solid fa-star"></i>
                    <i className="fa-solid fa-star"></i>
                    <i className="fa-solid fa-star"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom Contact Section */}
      <section id="kontakt-sektion" className="py-16 sm:py-24 bg-white scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 sm:gap-16 items-start">
            
            {/* Left Column: Company details and membership badges */}
            <div className="lg:col-span-5 space-y-8">
              <div className="space-y-4">
                <h3 className="text-2xl sm:text-3xl font-extrabold text-stone-900 uppercase">
                  Dansk Tagbyg ApS
                </h3>
                <div className="text-sm text-stone-700 space-y-2">
                  <p><strong>Ring:</strong> <a href={COMPANY_INFO.phoneTel} className="text-[#BE0F12] font-semibold hover:underline">{COMPANY_INFO.phoneFormatted}</a></p>
                  <p><strong>Skriv:</strong> <a href={COMPANY_INFO.emailMailto} className="text-[#BE0F12] font-semibold hover:underline">{COMPANY_INFO.email}</a></p>
                  <p><strong>Adr:</strong> {COMPANY_INFO.address}, {COMPANY_INFO.postalCity}</p>
                  <p><strong>CVR:</strong> {COMPANY_INFO.cvr}</p>
                  <p className="pt-2 font-bold text-stone-900">Vi løser opgaver i hele DK</p>
                </div>
              </div>

              {/* Memberships / Garanti-mærker */}
              <div className="p-6 bg-stone-50 border border-stone-200 space-y-3">
                <span className="text-xs font-bold uppercase tracking-wider text-stone-500">Vi er medlem af:</span>
                <div className="flex flex-wrap items-center gap-6 pt-2">
                  <img
                    src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/swisspearl-logo.png"
                    alt="Swisspearl Certificeret Tagpartner"
                    className="h-8 w-auto object-contain"
                  />
                  <img
                    src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/imgi-12-thumb.png"
                    alt="Byg Garanti"
                    className="h-8 w-auto object-contain"
                  />
                  <img
                    src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/imgi-11-thumb.png"
                    alt="DI Byggeri"
                    className="h-8 w-auto object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Right Column: Contact form */}
            <div className="lg:col-span-7 bg-stone-50 p-6 sm:p-10 border border-stone-200 shadow-sm space-y-6">
              <div className="space-y-2">
                <h3 className="text-2xl font-extrabold text-stone-900 uppercase">
                  Kontakt os via formularen
                </h3>
                <p className="text-xs sm:text-sm text-stone-600">
                  Vil du gerne have et uforpligtende tilbud på dit næste projekt, eller har du spørgsmål til, hvad vi kan hjælpe dig med? Du er altid velkommen til at kontakte os via kontaktformularen her, så vender vi tilbage hurtigst muligt.
                </p>
              </div>

              {submitStatus === 'success' ? (
                <div className="p-6 bg-emerald-50 border border-emerald-300 text-emerald-900 text-center space-y-2">
                  <i className="fa-solid fa-circle-check text-3xl text-emerald-600"></i>
                  <div className="font-bold text-lg">Mange tak for din henvendelse!</div>
                  <p className="text-sm">Vi har modtaget din besked og vender hurtigt tilbage.</p>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  {/* Honeypot */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <input
                      type="text"
                      name="website_url"
                      value={formState.website_url}
                      onChange={handleInputChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-stone-700 uppercase mb-1">Navn *</label>
                      <input
                        type="text"
                        name="Navn"
                        required
                        placeholder="Dit fulde navn"
                        value={formState.Navn}
                        onChange={handleInputChange}
                        className="w-full px-3.5 py-2.5 rounded-none bg-white border border-stone-300 text-stone-900 text-sm focus:ring-1 focus:ring-[#BE0F12] focus:border-[#BE0F12]"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-stone-700 uppercase mb-1">Mobil *</label>
                      <input
                        type="tel"
                        name="Mobil"
                        required
                        placeholder="Mobilnummer"
                        value={formState.Mobil}
                        onChange={handleInputChange}
                        className="w-full px-3.5 py-2.5 rounded-none bg-white border border-stone-300 text-stone-900 text-sm focus:ring-1 focus:ring-[#BE0F12] focus:border-[#BE0F12]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-stone-700 uppercase mb-1">Mail *</label>
                    <input
                      type="email"
                      name="Mail"
                      required
                      placeholder="Din e-mailadresse"
                      value={formState.Mail}
                      onChange={handleInputChange}
                      className="w-full px-3.5 py-2.5 rounded-none bg-white border border-stone-300 text-stone-900 text-sm focus:ring-1 focus:ring-[#BE0F12] focus:border-[#BE0F12]"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-bold text-stone-700 uppercase mb-1">Adresse *</label>
                      <input
                        type="text"
                        name="Adresse"
                        required
                        placeholder="Vejnavn og husnummer"
                        value={formState.Adresse}
                        onChange={handleInputChange}
                        className="w-full px-3.5 py-2.5 rounded-none bg-white border border-stone-300 text-stone-900 text-sm focus:ring-1 focus:ring-[#BE0F12] focus:border-[#BE0F12]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-stone-700 uppercase mb-1">Postnummer *</label>
                      <input
                        type="text"
                        name="Postnummer"
                        required
                        placeholder="Postnr."
                        value={formState.Postnummer}
                        onChange={handleInputChange}
                        className="w-full px-3.5 py-2.5 rounded-none bg-white border border-stone-300 text-stone-900 text-sm focus:ring-1 focus:ring-[#BE0F12] focus:border-[#BE0F12]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-stone-700 uppercase mb-1">Besked *</label>
                    <textarea
                      name="Besked"
                      rows="4"
                      required
                      placeholder="Beskriv dit projekt eller dine ønsker..."
                      value={formState.Besked}
                      onChange={handleInputChange}
                      className="w-full px-3.5 py-2.5 rounded-none bg-white border border-stone-300 text-stone-900 text-sm focus:ring-1 focus:ring-[#BE0F12] focus:border-[#BE0F12]"
                    ></textarea>
                  </div>

                  <FileUploadField
                    files={files}
                    onFilesChange={setFiles}
                    idPrefix="service-detail"
                    label="Vedhæft evt. 1-5 billeder af dit hus på afstand, hvor man kan se taget"
                  />

                  <div className="space-y-2 pt-2 text-xs text-stone-700">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="Finansiering"
                        checked={formState.Finansiering}
                        onChange={handleInputChange}
                        className="w-4 h-4 accent-[#BE0F12] rounded-none"
                      />
                      <span>Har du finansieringen på plads og ønsker et tilbud?</span>
                    </label>

                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="AccepterBetingelser"
                        required
                        checked={formState.AccepterBetingelser}
                        onChange={handleInputChange}
                        className="w-4 h-4 accent-[#BE0F12] rounded-none mt-0.5"
                      />
                      <span>Jeg har læst og accepteret vilkårene for brug af oplysninger og privatlivspolitik. *</span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3.5 bg-[#BE0F12] hover:bg-red-700 text-white font-extrabold text-sm uppercase tracking-wider shadow-md transition-colors cursor-pointer"
                  >
                    {submitting ? 'Sender...' : 'Send'}
                  </button>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <LightboxModal
        isOpen={lightboxOpen}
        images={galleryImages.length > 0 ? galleryImages : [service.image]}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setLightboxIndex((prev) => (prev === 0 ? (galleryImages.length || 1) - 1 : prev - 1))}
        onNext={() => setLightboxIndex((prev) => (prev === (galleryImages.length || 1) - 1 ? 0 : prev + 1))}
      />
    </div>
  );
}
